// src/pages/Officer/components/MetricsPanel.jsx
import React from 'react';
import { Box, Paper, Stack, Typography, alpha } from '@mui/material';
import {
  ShowChart,
  Notifications,
  CheckCircle,
  Speed,
  Timeline,
  TrendingUp,
} from '@mui/icons-material';

/**
 * MetricCard - Individual metric display
 */
const MetricCard = ({ title, value, subtitle, icon: Icon, color, trend }) => (
  <Paper
    elevation={0}
    sx={{
      p: 2.5,
      bgcolor: alpha('#0f1419', 0.6),
      border: '1px solid',
      borderColor: alpha(color, 0.2),
      borderRadius: 2,
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      position: 'relative',
      overflow: 'hidden',
      '&:hover': {
        borderColor: alpha(color, 0.4),
        transform: 'translateY(-2px)',
        boxShadow: `0 8px 24px ${alpha(color, 0.15)}`,
      },
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: '3px',
        background: `linear-gradient(90deg, ${color}, ${alpha(color, 0.5)})`,
      },
    }}
  >
    <Stack spacing={2}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
        <Box>
          <Typography
            variant="caption"
            sx={{
              color: 'grey.500',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.1em',
              fontSize: '0.7rem',
            }}
          >
            {title}
          </Typography>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              color: 'grey.50',
              mt: 0.5,
              fontFamily: 'monospace',
              letterSpacing: '-0.02em',
            }}
          >
            {value}
          </Typography>
          {subtitle && (
            <Typography
              variant="caption"
              sx={{
                color: 'grey.600',
                mt: 0.5,
                display: 'block',
              }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: 1.5,
            bgcolor: alpha(color, 0.1),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '1px solid',
            borderColor: alpha(color, 0.2),
          }}
        >
          <Icon sx={{ color, fontSize: 24 }} />
        </Box>
      </Stack>

      {trend && (
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <TrendingUp
            sx={{
              fontSize: 16,
              color: trend > 0 ? '#00ff88' : '#ff4444',
              transform: trend < 0 ? 'rotate(180deg)' : 'none',
            }}
          />
          <Typography
            variant="caption"
            sx={{
              color: trend > 0 ? '#00ff88' : '#ff4444',
              fontWeight: 600,
              fontFamily: 'monospace',
            }}
          >
            {Math.abs(trend).toFixed(1)}% vs last period
          </Typography>
        </Stack>
      )}
    </Stack>
  </Paper>
);

/**
 * MetricsPanel - Display key performance metrics
 */
const MetricsPanel = ({ metrics }) => {
  const metricCards = [
    {
      title: 'Active Strategies',
      value: metrics.activeStrategies,
      subtitle: `${metrics.liveStrategies} broadcasting`,
      icon: Speed,
      color: '#00d4ff',
    },
    {
      title: 'Signals Today',
      value: metrics.totalSignals,
      subtitle: 'Total detected',
      icon: Notifications,
      color: '#ffaa00',
    },
    {
      title: 'Success Rate',
      value: `${metrics.successRate}%`,
      subtitle: 'Last 7 days',
      icon: CheckCircle,
      color: '#00ff88',
    },
    {
      title: 'Live Alerts',
      value: metrics.totalAlerts,
      subtitle: 'In session',
      icon: Timeline,
      color: '#ff4444',
    },
  ];

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          sm: 'repeat(2, 1fr)',
          lg: 'repeat(4, 1fr)',
        },
        gap: 2,
      }}
    >
      {metricCards.map((metric, index) => (
        <MetricCard key={index} {...metric} />
      ))}
    </Box>
  );
};

export default MetricsPanel;
